﻿<script lang="ts">
    import { AccordionContent, Button, Icon, List, ListItem, Preloader, f7 } from 'framework7-svelte';
    import { createEventDispatcher } from 'svelte';
    import { tick } from 'svelte';
    import { _ } from 'svelte-i18n';
    
    import { getLegendColorRGB, getLegendColorString } from '../../utils/style';
    import type {RouteEditorRoute} from "../../models/route-editor-route.model";
    import type {RouteEditorGate} from "../../models/route-editor-gate.model";

    const dispatch = createEventDispatcher<{
        save: void;
        changed: void;
        close: void;
        addRoute: void;
        addGate: void;
        editRouteName: void;
        editGateName: void;
        deleteRoute: void;
        deleteGate: void;
        editGateLocation: void;
    }>();

    export let routes: RouteEditorRoute[];
    export let gates: RouteEditorGate[];

    let component: HTMLElement;

    $: sortedRoutes = routes.toSorted((a, b) => a.name.localeCompare(b.name));

    let drawGateLists = true;
    export let isSaving = false;
    export let isAddingGate = false;

    export let hasChanges = false;

    export let selectedRoute: RouteEditorRoute | null;
    export let selectedGate: RouteEditorGate | null;
    export let selectedGateIndex: number | null;

    export let highlightedGate: RouteEditorGate | null;

    export let gatesByName: Map<string, RouteEditorGate>;
    
    let highlightedResetTimer;

    let openRouteNames: Map<string, boolean> = new Map();

    $: routeColors = Object.fromEntries(routes.map((route, index) => [route.name, getLegendColorString(getLegendColorRGB(index))]));

    $: routeGates = Object.fromEntries(routes.map((route) => [route.name, route.gateNames]));

    $: bringSelectedRouteAndGateIntoFocus(selectedRoute, selectedGate);

    async function handleGateDrag(event: CustomEvent, route: RouteEditorRoute) {
        const item = route.gateNames.splice(event.detail[0].from, 1)[0];
        route.gateNames.splice(event.detail[0].to, 0, item);

        markChanged();
        await refreshGateLists();
    }

    async function bringSelectedRouteAndGateIntoFocus(selectedRoute: RouteEditorRoute, selectedGate: RouteEditorGate) {
        await tick();
        if (selectedGate) {
            const g = component.querySelector('.selected-gate');
            g?.scrollIntoView({ behavior: 'smooth' });
        } else if (selectedRoute) {
            const r = component.querySelector('.selected-route');
            r?.scrollIntoView({ behavior: 'smooth' });
        }
    }

    function handleRouteClick(event: CustomEvent | undefined, route: RouteEditorRoute) {
        const detail = event.detail as CustomEvent;
        const fromButton = (detail.target as HTMLElement)?.closest('.accordion-item-button');
        if (fromButton) {
            return;
        }
        
        const routeOpen = openRouteNames.get(route.name);
        if (routeOpen && route.name === selectedRoute?.name) {
            openRouteNames.delete(route.name);
            openRouteNames = openRouteNames;
            if (route.name === selectedRoute?.name) {
                selectedRoute = null;
            }
        } else {
            selectedRoute = route;
            openRouteNames.set(route.name, true);
        }
        
        selectedGate = null;
        selectedGateIndex = null;
    }

    function handleGateClick(route: RouteEditorRoute, gate: RouteEditorGate, gateIndex: number) {
        selectedRoute = route;
        selectedGate = gate;
        selectedGateIndex = gateIndex;
    }

    function markChanged() {
        dispatch('changed');
    }

    function highlightGate(gate: RouteEditorGate) {
        // We use a timer so that when the user has a gate selected, and the mouse goes out of the current
        // highlighted gate and he is underway to another highlighted gate, the map does not jump
        // back to the selected gate in between.

        if (highlightedResetTimer) {
            clearTimeout(highlightedResetTimer);
            highlightedResetTimer = undefined;
        }
        highlightedGate = gate;
    }

    function unhighlightGate() {
        highlightedResetTimer = setTimeout(() => {
            highlightedGate = null;
        }, 1500);
    }

    /**
     * Since we're updating the list state order, the list items don't point to the correct gate anymore.
     * This function forces the list to redraw, so the correct gate is shown.
     */
    async function refreshGateLists() {
        gates = gates;
        routes = routes;

        drawGateLists = false;
        await tick();
        drawGateLists = true;
    }

    function preventOpening(ev: CustomEvent) {
        // ev.detail[0] is a prevent function that, when invoked, which is what we do,
        // prevents opening of the accordion. We want to prevent that because we use handleRouteClick for this.
        ev.detail[0]();
    }
</script>

<div class="route-editor-parent" bind:this={component}>
    <div class="title-parent">
        <p class="control-title">
            {$_('pages.route-editor.ROUTE_MANAGEMENT')}
        </p>
    </div>
    <div class="scroll-parent">
        <List accordionList dividers>
            {#each sortedRoutes as route, routeIndex}
                <ListItem
                    accordionItem
                    title={route.name}
                    class={selectedRoute?.name === route.name && selectedGate === null ? 'selected-accordion-item selected-route' : ''}
                    style="--f7-list-chevron-icon-color: {selectedRoute?.name === route.name && selectedGate === null ? 'white' : 'black'}"
                    accordionItemOpened={selectedRoute?.name === route.name || openRouteNames.get(route.name)}
                    on:accordionBeforeOpen={(ev) => preventOpening(ev)}
                    on:accordionBeforeClose={(ev) => preventOpening(ev)}
                    on:click={(e) => handleRouteClick(e, route)}
                >
                    <Icon f7="circle_fill" slot="media" class="route-color-icon" style={`color: ${routeColors[route.name]};`} />

                    <div slot="after-title" class="accordion-button-container">
                        {#if selectedRoute?.name === route.name && selectedGate === null}
                            <Button
                                iconMaterial="Edit"
                                iconColor="white"
                                iconSize="18px"
                                class="accordion-item-button"
                                on:click={() => {
                                    dispatch('editRouteName');
                                }}
                            />
                            <Button
                                iconMaterial="Delete"
                                iconColor="white"
                                iconSize="18px"
                                class="accordion-item-button"
                                on:click={() => {
                                    dispatch('deleteRoute');
                                }}
                            />
                        {/if}
                    </div>

                    <AccordionContent>
                        {#if drawGateLists}
                            <List
                                on:sortableSort={(e) => handleGateDrag(e, route)}
                                sortable
                                sortableEnabled
                                sortableOpposite
                                dividers
                                class="accordion-content-list"
                            >
                                {#each routeGates?.[route.name] as gateName, gateIndex}
                                    <ListItem
                                        style="--f7-sortable-handler-color: {
                                            selectedGate?.name === gateName 
                                            && selectedRoute?.name === route.name 
                                             && selectedGateIndex === gateIndex 
                                                ? 'white' 
                                                : 'black'
                                            }"
                                        class="{
                                            selectedGate?.name === gateName 
                                            && selectedRoute?.name === route.name 
                                            && selectedGateIndex === gateIndex 
                                                ? 'selected-list-item selected-gate' 
                                                : ''} clickable"
                                        on:click={(e) => handleGateClick(route, gatesByName.get(gateName), gateIndex)}
                                    >
                                        <div slot="after-title" class="accordion-button-container">
                                            {#if selectedRoute?.name === route.name && selectedGate?.name === gateName}
                                                <Button
                                                    iconMaterial="Edit"
                                                    iconColor="white"
                                                    iconSize="18px"
                                                    class="accordion-item-button"
                                                    on:click={() => {
                                                        dispatch('editGateName');
                                                    }}
                                                />
                                                <Button
                                                    iconMaterial="my_location"
                                                    iconColor="white"
                                                    iconSize="18px"
                                                    class="accordion-item-button"
                                                    on:click={() => {
                                                        dispatch('editGateLocation');
                                                    }}
                                                />
                                                <Button
                                                    iconMaterial="Delete"
                                                    iconColor="white"
                                                    iconSize="18px"
                                                    class="accordion-item-button"
                                                    on:click={() => {
                                                        dispatch('deleteGate');
                                                    }}
                                                />
                                            {/if}
                                        </div>
                                        <div slot="title" on:mouseenter={() => highlightGate(gatesByName.get(gateName))} on:mouseleave={() => unhighlightGate()}>
                                            {gatesByName.get(gateName).name}
                                        </div>
                                    </ListItem>
                                {/each}
                            </List>
                        {/if}
                    </AccordionContent>
                </ListItem>
            {/each}
        </List>
    </div>
    <div class="button-parent">
        <Button class="secondary-button" disabled={isSaving || isAddingGate} on:click={() => dispatch('addRoute')}>
            <Icon material="Add" size="18px" />
            {$_('pages.route-editor.ROUTE')}
        </Button>
        <Button class="secondary-button" disabled={isSaving || isAddingGate || (!selectedRoute && !selectedGate)} on:click={() => dispatch('addGate')}>
            <Icon material="Add" size="18px" />
            {$_('pages.route-editor.GATE')}
        </Button>
        <Button class="save-button" fill on:click={() => dispatch('save')} disabled={isSaving || isAddingGate || !hasChanges}>
            {#if isSaving}
                <Preloader color="white" size={24} class="saving-preloader" />
            {/if}
            {$_('shared.SAVE')}
        </Button>
    </div>
</div>

<style lang="scss">
    @import '../../assets/scss/colors';

    .title-parent {
        height: 56px;
        padding: 8px;
    }

    .scroll-parent {
        overflow: scroll;
        flex-grow: 1;
        height: 100%;
    }

    .button-parent {
        background: #{$surface-container};
        height: 80px;
        display: flex;
        flex-direction: row;
        gap: 16px;
        padding: 16px;
        margin: -12px -12px 0 -12px;
        align-items: center;
    }

    .route-editor-parent {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .control-title {
        font-size: 14pt;
        font-weight: 500;
        color: #{$surface-on-variant};
    }

    .accordion-button-container {
        display: flex;
        flex-direction: row;
    }

    :global(.saving-preloader) {
        margin-right: 16px;
    }
</style>
